import { isEmpty, objectUtils } from '@festina-life/core'
import {
  Field,
  Radio,
  RadioGroup as HeadlessRadioGroup,
} from '@headlessui/react'
import cx from 'classnames'
import type { FocusEvent, ReactNode } from 'react'

import { focusRingStyles } from '../../libs/focusRingStyles'
import { paperStyles } from '../../libs/paperStyles'
import {
  disabledInputStyles,
  inputTypographyStyles,
} from '../textInput/rawTextInputStyles'

export type RawRadioGroupProps<T> = {
  options: readonly T[]
  renderFn?: (value: NoInfer<T>) => string | ReactNode
  keyFn?: (value: NoInfer<T>) => string
  value?: NoInfer<T>
  onChange: (nextValue: NoInfer<T>) => void
  onBlur?: (event: FocusEvent<HTMLDivElement>) => void
  className?: string
  optionClassName?: string
  isDisabled?: ((value: T) => boolean) | boolean
  isInError?: boolean
  id?: string
  'aria-label'?: string
}

export const RawRadioGroup = <T,>({
  id,
  'aria-label': ariaLabel,
  options,
  value,
  onChange,
  onBlur,
  renderFn = (entry: T) => `${entry}`,
  keyFn = (entry: T) => objectUtils.objectToSortedString(entry),
  className,
  optionClassName,
  isDisabled,
  isInError = false,
}: RawRadioGroupProps<T>) => {
  const isGroupDisabled =
    typeof isDisabled === 'boolean' ? isDisabled : undefined

  const groupStates =
    `${isInError ? 'error' : 'noError'}${isGroupDisabled ? 'Disabled' : 'Enabled'}` as const

  return (
    <HeadlessRadioGroup
      aria-label={ariaLabel}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      data-autofocus
      className={cx(
        'grid grid-flow-col gap-0.5 rounded-lg p-0.5',
        {
          noErrorDisabled: disabledInputStyles({ isInError: false }),
          noErrorEnabled: paperStyles({ isFaded: true, ringColor: 'grey' }),
          errorDisabled: disabledInputStyles({ isInError: true }),
          errorEnabled: 'text-error-500 bg-current/10',
        }[groupStates],
        className,
      )}
      disabled={isGroupDisabled}
      id={id}
      by={
        (a, b) => (isEmpty(a) || isEmpty(b) ? a === b : keyFn(a) === keyFn(b)) // The by function also needs to handle undefined values, unlike the keyFn
      }
    >
      {options.map((option) => {
        const isOptionDisabled =
          typeof isDisabled === 'function' ? isDisabled(option) : !!isDisabled

        return (
          <Field
            key={keyFn(option)}
            data-testid={id ? `${id}-option-${keyFn(option)}` : undefined}
          >
            <Radio
              value={option}
              disabled={isOptionDisabled}
              className={({ checked }) =>
                cx(
                  'flex items-center justify-center px-2 py-1 rounded-[calc(var(--radius-lg)-var(--spacing)*0.5)] transition',
                  inputTypographyStyles({ isDisabled: isOptionDisabled }),
                  focusRingStyles({ hasError: isInError }),
                  checked
                    ? cx(paperStyles(), 'shadow')
                    : !isOptionDisabled &&
                        'hover:bg-amaze-paper hover:dark:bg-amaze-paperDark',
                  isInError && 'ring-error-500! text-error-500',
                  isOptionDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
                  isGroupDisabled && 'opacity-50',
                  optionClassName,
                )
              }
            >
              {renderFn(option)}
            </Radio>
          </Field>
        )
      })}
    </HeadlessRadioGroup>
  )
}
